import React, { useCallback, useEffect, useState } from "react";
import { fetchDuplicates } from "./mergeApi";
import { typeLabel, SELF_EXPERT_SOURCE_TOOLTIP } from "./constants";
import LinkedValue from "./LinkedValue";

// Normalize a duplicate group across the two API shapes: MediaOutlet groups by
// `domain` with `outlets`; PromoEntity groups by `name` with `entities`.
function normalizeGroup(group) {
  return {
    label: group.domain || group.name || "(unnamed)",
    count: group.count,
    members: group.outlets || group.entities || [],
  };
}

function MemberRow({ member, onPick }) {
  const isSelfExpert = member.self_expert === true;
  const links = member.links || {};
  // For the promo-entity types the admin destination IS the public page (see
  // Admin::Merging::PreviewLinks#id_link), so offering both would be the same link
  // twice. Only MediaOutlet, which has its own admin page, gets the second one.
  const publicHref = links.slug && links.slug !== links.id ? links.slug : null;

  return (
    <li className="list-group-item d-flex justify-content-between align-items-center">
      <span>
        <LinkedValue href={links.id} testid={`dup-link-id-${member.id}`}>
          <span className="fw-medium">{member.name || `#${member.id}`}</span>
        </LinkedValue>
        <span className="text-muted ms-2">#{member.id}</span>
        {publicHref && (
          <a
            href={publicHref}
            target="_blank"
            rel="noopener noreferrer"
            className="ms-2 small"
            data-testid={`dup-link-slug-${member.id}`}
          >
            Public page
          </a>
        )}
        {member.migrate_id != null && (
          <LinkedValue href={links.migrate_id} testid={`dup-link-migrate-id-${member.id}`}>
            <span className="text-muted ms-2 small">V1 {member.migrate_id}</span>
          </LinkedValue>
        )}
        {isSelfExpert && (
          <span className="badge bg-warning text-dark ms-2" data-testid={`dup-self-expert-${member.id}`}>
            Self-expert
          </span>
        )}
      </span>
      <span className="btn-group btn-group-sm">
        {/* Wrapping span keeps the tooltip when the source button is disabled. */}
        <span title={isSelfExpert ? SELF_EXPERT_SOURCE_TOOLTIP : undefined}>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => onPick("source", member.id, member.name)}
            disabled={isSelfExpert}
            data-testid={`pick-source-${member.id}`}
          >
            Use as source
          </button>
        </span>
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={() => onPick("destination", member.id, member.name)}
          data-testid={`pick-destination-${member.id}`}
        >
          Use as destination
        </button>
      </span>
    </li>
  );
}

/**
 * DuplicatesPanel — helper that lists exact-name/exact-domain duplicate groups
 * for the selected type. Picking a member fills the source or destination id
 * input in the parent. A self-representing expert can only be picked as a
 * destination (its "Use as source" button is disabled with an explanatory tooltip).
 */
function DuplicatesPanel({ type, onPick }) {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!type) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDuplicates({ type });
      setGroups(data.map(normalizeGroup));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="card mt-3" data-testid="duplicates-panel">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <span>
          <i className="fa fa-clone me-2" />
          Duplicate {typeLabel(type)} candidates
        </span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <div className="card-body">
        {loading && <p className="text-muted mb-0">Finding duplicates…</p>}
        {error && !loading && (
          <div className="alert alert-danger mb-0">
            <i className="fa fa-exclamation-circle me-2" />
            {error}
          </div>
        )}
        {!loading && !error && groups && groups.length === 0 && (
          <p className="text-muted mb-0" data-testid="duplicates-empty">
            No duplicate groups found for this type.
          </p>
        )}
        {!loading && !error && groups && groups.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="fw-bold mb-1">
                  {group.label} <span className="badge bg-secondary rounded-pill">{group.count}</span>
                </div>
                <ul className="list-group">
                  {group.members.map((member) => (
                    <MemberRow key={member.id} member={member} onPick={onPick} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DuplicatesPanel;
